import type { CartPricing } from "@/features/cart/types/cart-pricing.type";
import type { DiscountType } from "@/features/cart/types/discount.type";
import type { DietCatalog, DietCatalogDetail } from "@/features/diets/types";

// Omnibus (MPL-1835), contract: docs/omnibus-cena-30-dni.md. The backend always
// returns lowestPriceLast30Days (L); the shop switches to the Omnibus
// presentation of the price only with this flag on, no earlier than 31 days
// after the backend started logging prices.
export const isOmnibusEnabled = () =>
  process.env.NEXT_PUBLIC_OMNIBUS_ENABLED === "true";

export type OmnibusPresentation =
  | { promotion: false }
  | {
      promotion: true;
      // L, per day.
      lowestPrice: number;
    };

const NO_PROMOTION: OmnibusPresentation = { promotion: false };

const toGrosze = (value: number) => Math.round(value * 100);

// Contract pt 7: a price reduction may be signalled only when L is above the
// reduced price. Compared in grosze so floating point noise cannot flip the
// outcome.
export const getOmnibusPresentation = ({
  price,
  lowestPrice,
}: {
  // Reduced price of the offer without codes, per day.
  price: number;
  lowestPrice?: number | null;
}): OmnibusPresentation => {
  if (lowestPrice == null || toGrosze(lowestPrice) <= toGrosze(price)) {
    return NO_PROMOTION;
  }

  return { promotion: true, lowestPrice };
};

// Reduced 1-day price of a listing item and its L, handed from the diet card
// to views that load the diet on their own (menu preview).
export type OmnibusListingPrices = {
  promoPrice?: number | null;
  lowestPrice?: number | null;
};

// Menu preview opened from the diet configurator: the regular and reduced
// per-day price of the configured quote with its L, as in the summary.
export type OmnibusConfiguredPrices = OmnibusListingPrices & {
  price: number;
};

// Diet card. Only search results carry the reduced 1-day price and its L; a
// price passed in by the caller comes from another quote, so the card then has
// no Omnibus low and signals no reduction.
export const getDietCardOmnibus = ({
  diet,
  price,
}: {
  diet: DietCatalog | DietCatalogDetail;
  price?: number;
}) => {
  const fromSearch = price === undefined && "lowestPriceLast30Days" in diet;
  const promoPrice = fromSearch ? diet.promoPrice : undefined;
  const lowestPrice = fromSearch ? diet.lowestPriceLast30Days : undefined;

  return {
    promoPrice,
    lowestPrice,
    presentation: getOmnibusPresentation({
      price: promoPrice ?? price ?? diet.basePrice,
      lowestPrice,
    }),
  };
};

const isCode = (discount: DiscountType) => discount.type === "manual";

// Cart and checkout position. L is compared with the diet's reduced price per
// day without codes, so code discounts are added back to the quoted final
// price (pt 7d).
export const getCartItemOmnibusPresentation = ({
  pricing,
  days,
  lowestPrice,
}: {
  pricing: CartPricing;
  days: number;
  lowestPrice?: number | null;
}): OmnibusPresentation => {
  const codesAmount = (pricing.discounts ?? [])
    .filter(isCode)
    .reduce((sum, code) => sum + code.amount.gross, 0);

  return getOmnibusPresentation({
    price: (pricing.final.gross + codesAmount) / (days || 1),
    lowestPrice,
  });
};

// Codes are not Omnibus promotions (contract pt 4): a code-reduced price is
// still shown as discounted, with or without L.
export const getOmnibusDiscountCodes = (discountCodes?: DiscountType[]) =>
  discountCodes?.filter(isCode);
